/**
 * useAppAvailabilityPoll Hook
 *
 * This custom React hook polls an endpoint to check the availability status of the IAR (Internet Access Registration) application.
 * It tracks whether an outage is happening and attempts to retry failed requests with exponential backoff.
 *
 * @param {string} url - The endpoint URL to poll for app availability status
 * @param {Function} lockAwareFetch - Fetch wrapper used for polling requests
 * @returns {boolean} isOutageHappened - True if the app is not live, false if the app is live or polling hasn't started
 *
 * Behavior:
 * - Polls the URL every 20 seconds
 * - Sends the user's Okta access token with each request
 * - On successful response, checks if data equals "IAR is live" or "IAR is not live"
 * - On error, retries with exponential backoff (2s, 4s, 8s... up to 30s max)
 * - Cleans up polling interval on component unmount
 */

import { useEffect, useRef, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'

// Polling interval in milliseconds: 20 seconds
const pollingIntervalMs = 20 * 1000

/**
 * Custom hook that polls for app availability
 * @param {string} url - The availability check endpoint
 * @returns {boolean} Whether an outage is happening
 */
export default function useAppAvailabilityPoll(url, lockAwareFetch) {
  const [isOutageHappened, toggleOutageBoolean] = useState(false)

  const { oktaAuth } = useOktaAuth()

  const pollingFnRef = useRef(() => {})
  const retryIntervalMs = useRef(2 * 1000)

  /**
   * useEffect Hook - Initializes polling on component mount
   *
   * Dependency Array: [] (empty)
   * - Runs ONLY once when the component mounts
   * - The empty array ensures polling is not reinitialized on every render
   *
   * Cleanup Function: clearInterval(pollingFnRef.current)
   * - Clears the polling interval when the component unmounts
   * - Prevents memory leaks by stopping the setInterval timer
   */
  useEffect(() => {
    /**
     * getAppAvailabilityInfo Function
     *
     * Makes a single fetch request to check the app's availability status.
     * Handles both success and error responses with different logic.
     *
     * Success Path (.then):
     * - Extracts the 'data' property from the response
     * - Resets retryIntervalMs.current to 2 seconds for the next error retry
     * - Sets isOutageHappened to false if data === 'IAR is live'
     * - Sets isOutageHappened to true if data === 'IAR is not live'
     *
     * Error Path (.catch):
     * - Logs the error to console for debugging
     * - Warns about the failed poll attempt and the retry delay
     * - Clears the existing interval to stop continuous polling
     * - Waits retryIntervalMs.current (2s, 4s, 8s, etc.) before calling initPolling()
     * - Doubles the retry interval (capped at 30 seconds max) for exponential backoff
     */
    const getAppAvailabilityInfo = async () => {
      const {
        accessToken: { accessToken: token },
      } = await oktaAuth.tokenManager.getTokens()

      lockAwareFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
        .then(response => {
          const { data } = response

          retryIntervalMs.current = 2 * 1000
          if (data === 'IAR is live') {
            toggleOutageBoolean(false)
          } else if (data === 'IAR is not live') {
            toggleOutageBoolean(true)
          }
        })
        .catch(error => {
          console.error('Error during polling:', error)

          console.warn(`Polling failed, retrying in ${retryIntervalMs.current / 1000} seconds...`)

          clearInterval(pollingFnRef.current)

          setTimeout(() => {
            initPolling()
          }, retryIntervalMs.current)

          retryIntervalMs.current = Math.min(retryIntervalMs.current * 2, 30_000)
        })
    }

    /**
     * initPolling Function
     *
     * Initializes the continuous polling mechanism.
     *
     * Step 1: Calls getAppAvailabilityInfo() immediately for the first check
     * Step 2: Sets up a recurring interval that:
     *   - Calls getAppAvailabilityInfo() every 20 seconds (pollingIntervalMs)
     *   - Stores the interval ID in pollingFnRef.current for cleanup later
     */
    const initPolling = () => {
      getAppAvailabilityInfo()

      pollingFnRef.current = setInterval(() => {
        getAppAvailabilityInfo()
      }, pollingIntervalMs)
    }

    initPolling()

    return () => clearInterval(pollingFnRef.current)
  }, [])

  return isOutageHappened
}
